import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowDownLeft, ArrowUpRight, CheckCircle2, Clock, XCircle, MessageCircle, LogIn } from "lucide-react";
import { BottomNav } from "@/components/BottomNav";
import { useAuth } from "@/hooks/useAuth";
import { apiService } from "@/services/api";
import { analyticsService } from "@/services/analytics";
import { TRANSACTIONS, PLATFORM_ACCENT, type Transaction } from "@/lib/app-data";

function StatusBadge({ status }: { status: Transaction["status"] }) {
  const map = {
    success: { label: "Réussi", color: "#0052cc", Icon: CheckCircle2 },
    pending: { label: "En cours", color: "#eab308", Icon: Clock },
    failed: { label: "Échec", color: "#ef4444", Icon: XCircle },
  } as const;
  const { label, color, Icon } = map[status];
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
      style={{ background: `${color}22`, color }}
    >
      <Icon className="size-3" /> {label}
    </span>
  );
}

// Map API Transaction (types/index.ts) → app-data Transaction shape for rendering
function mapApiTx(t: any): Transaction {
  const isDeposit = t.type === "deposit";
  const platform = (String(t.platform || "1XBET")).toUpperCase() as Transaction["platform"];
  const statusMap: Record<string, Transaction["status"]> = {
    completed: "success",
    processing: "pending",
    pending: "pending",
    failed: "failed",
  };
  return {
    id: String(t.id),
    ref: `ES-${String(t.id).slice(-6).toUpperCase()}`,
    type: isDeposit ? "deposit" : "withdraw",
    platform: (["1XBET", "MELBET", "BETWINNER"].includes(platform) ? platform : "1XBET") as Transaction["platform"],
    amount: Number(t.amount || 0),
    status: statusMap[t.status] || "pending",
    createdAt: t.createdAt ? new Date(t.createdAt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—",
    accountId: t.accountId || "—",
  };
}

function TransactionsPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<Transaction[]>(TRANSACTIONS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    analyticsService.trackScreenView("transactions_page");
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiService.getTransactions(user.id);
        if (cancelled) return;
        if (res.success && res.data && res.data.length > 0) {
          setItems(res.data.map(mapApiTx));
        } else if (res.error) {
          // keep fallback, surface a soft error
          setError(res.error);
        } else {
          // No transactions yet — show empty state (keep fallback so list isn't empty)
          setItems(TRANSACTIONS);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Erreur de chargement");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id]);

  const whatsappBase = "https://wa.me/22605756806";

  return (
    <div className="relative flex min-h-[100dvh] flex-col bg-background text-foreground [padding-top:env(safe-area-inset-top)]">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 0%, var(--page-gradient-via) 0%, var(--page-gradient-from) 45%, var(--page-gradient-to) 100%)",
        }}
      />
      <header className="sticky top-0 z-30 border-b border-border bg-header-bg backdrop-blur-xl">
        <div className="mx-auto flex max-w-[52ch] items-center gap-3 px-4 py-3 lg:max-w-6xl lg:px-8">
          <Link to="/" aria-label="Retour" className="grid size-9 place-items-center rounded-full bg-muted ring-1 ring-card-muted-ring">
            <ArrowLeft className="size-4" />
          </Link>
          <h1 className="text-base font-semibold tracking-tight">Historique</h1>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[52ch] flex-1 px-4 py-4 lg:max-w-6xl lg:px-8">
        {!isAuthenticated && (
          <div className="mb-3 rounded-2xl bg-card-muted p-4 text-center ring-1 ring-card-muted-ring">
            <p className="text-sm font-medium">Connectez-vous pour voir votre historique réel.</p>
            <button
              onClick={() => navigate("/login")}
              className="mt-3 inline-flex items-center gap-2 rounded-xl bg-accent-brand px-4 py-2 text-sm font-semibold text-white"
            >
              <LogIn className="size-4" /> Se connecter
            </button>
            <p className="mt-2 text-[11px] text-text-subtle">Vous regardez des données d'exemple ci-dessous.</p>
          </div>
        )}

        {loading && (
          <div className="rounded-2xl bg-card-muted p-6 text-center text-sm text-muted-foreground ring-1 ring-card-muted-ring">
            Chargement de votre historique…
          </div>
        )}

        {error && !loading && (
          <div className="mb-3 rounded-2xl bg-red-500/10 p-3 text-center text-[11px] text-red-300 ring-1 ring-red-500/30">
            {error} — affichage des données d'exemple.
          </div>
        )}

        <ul className="space-y-2">
          {items.map((t) => {
            const isDep = t.type === "deposit";
            const accent = PLATFORM_ACCENT[t.platform];
            const Icon = isDep ? ArrowDownLeft : ArrowUpRight;
            return (
              <li key={t.id} className="rounded-2xl bg-card-muted p-3.5 ring-1 ring-card-muted-ring">
                <div className="flex items-start gap-3">
                  <span
                    className="grid size-10 shrink-0 place-items-center rounded-xl"
                    style={{ background: `${accent}22`, color: accent }}
                  >
                    <Icon className="size-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-semibold">
                        {isDep ? "Dépôt" : "Retrait"} · {t.platform}
                      </p>
                      <p className="whitespace-nowrap text-sm font-bold tabular-nums">
                        {t.amount.toLocaleString("fr-FR")} <span className="text-xs text-muted-foreground">FCFA</span>
                      </p>
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-text-subtle">
                      <span className="truncate">
                        <span className="font-mono">{t.ref}</span> · ID {t.accountId}
                      </span>
                      <span className="whitespace-nowrap">{t.createdAt}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <StatusBadge status={t.status} />
                      {t.status !== "success" && (
                        <a
                          href={`${whatsappBase}?text=${encodeURIComponent(`Bonjour, aide sur transaction ${t.ref}`)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-foreground"
                        >
                          <MessageCircle className="size-3.5" /> Support
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
          {items.length === 0 && !loading && (
            <li className="rounded-2xl bg-card-muted p-6 text-center text-sm text-muted-foreground ring-1 ring-card-muted-ring">
              Aucune transaction pour le moment.
            </li>
          )}
        </ul>
      </main>

      <BottomNav />
    </div>
  );
}

export default TransactionsPage;